
import { supabase } from '@/integrations/supabase/client';
import { Project, ProjectMilestone, ProjectStakeholder } from './types';

export const projectService = {
  // Obtener todos los proyectos del usuario (propios + asignados)
  async getProjects(): Promise<Project[]> {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .order('created_at', { ascending: false });
      
    if (error) {
      console.error('Error al obtener proyectos:', error);
      return [];
    }
    
    return data as Project[];
  },
  
  // Obtener un proyecto por su ID
  async getProjectById(id: string): Promise<Project | null> {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', id)
      .single();
      
    if (error) {
      console.error(`Error al obtener el proyecto ${id}:`, error);
      return null;
    }
    
    return data as Project;
  },
  
  // Crear un nuevo proyecto
  async createProject(project: Omit<Project, 'id' | 'created_at' | 'updated_at'>): Promise<Project | null> {
    
    try {
      // Get current user
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError || !userData?.user) {
        console.error('User not authenticated:', userError);
        throw new Error('User must be authenticated to create a project');
      }

      const owner_id = userData.user.id;
      
      // Ensure valid status, priority, phase values based on database constraints
      const normalizedStatus = this.normalizeStatus(project.status);
      const normalizedPriority = this.normalizePriority(project.priority);
      const normalizedPhase = this.normalizePhase(project.phase);
      
      const validProject = {
        name: project.name || '',
        description: project.description || null,
        status: normalizedStatus,
        priority: normalizedPriority,
        phase: normalizedPhase,
        progress: project.progress || 0,
        risk_score: project.risk_score || 0,
        due_date: project.due_date || null,
        predictive_insights: project.predictive_insights || null,
        objectives: project.objectives || null,
        scope: project.scope || null,
        resources: project.resources || null,
        schedule: project.schedule || null,
        risks: project.risks || null,
        stakeholders: project.stakeholders || null,
        communication_plan: project.communication_plan || null,
        change_management: project.change_management || null,
        evaluation: project.evaluation || null,
        lessons_learned: project.lessons_learned || null,
        formal_closure: project.formal_closure || false,
        budget: project.budget || 0,
        benefit: project.benefit || 0,
        owner_id // Asegurar que owner_id está establecido
      };
      
      
      const { data, error } = await supabase
        .from('projects')
        .insert(validProject)
        .select()
        .single();
        
      if (error) {
        console.error('Error al crear proyecto:', error);
        throw error;
      }
      
      return data as Project;
    } catch (error) {
      console.error('Error en createProject:', error);
      throw error;
    }
  },
  
  // Helper methods to normalize values based on database constraints
  normalizeStatus(status: string | null | undefined): string {
    if (!status) return "En progreso";
    
    // Map form values to database allowed values
    const statusMap: Record<string, string> = {
      "Planificado": "En progreso", // Convertimos Planificado a En progreso
      "En progreso": "En progreso",
      "En pausa": "En pausa",
      "Completado": "Completado",
      "Cancelado": "Cancelado"
    };
    
    return statusMap[status] || "En progreso";
  },
  
  normalizePriority(priority: string | null | undefined): string {
    if (!priority) return "Media";
    
    const priorityMap: Record<string, string> = {
      "Baja": "Baja",
      "Media": "Media",
      "Alta": "Alta",
      "Crítica": "Alta" // Convertimos Crítica a Alta si no está permitido
    };
    
    return priorityMap[priority] || "Media";
  },
  
  normalizePhase(phase: string | null | undefined): string {
    if (!phase) return "Planificación";
    
    const phaseMap: Record<string, string> = {
      "Planificación": "Planificación",
      "Ejecución": "Ejecución",
      "Cierre": "Cierre"
    };
    
    return phaseMap[phase] || "Planificación";
  },
  
  // Actualizar un proyecto existente
  async updateProject(id: string, project: Partial<Project>): Promise<Project | null> {
    
    try {
      // Normalize values if present
      const projectToUpdate: Partial<Project> = {
        ...project
      };
      
      if (project.status) {
        projectToUpdate.status = this.normalizeStatus(project.status);
      }
      
      if (project.priority) {
        projectToUpdate.priority = this.normalizePriority(project.priority);
      }
      
      if (project.phase) {
        projectToUpdate.phase = this.normalizePhase(project.phase);
      }
      
      const { data, error } = await supabase
        .from('projects')
        .update(projectToUpdate)
        .eq('id', id)
        .select()
        .single();
        
      if (error) {
        console.error(`Error al actualizar el proyecto ${id}:`, error);
        throw error;
      }
      
      return data as Project;
    } catch (error) {
      console.error(`Error en updateProject para ${id}:`, error);
      throw error;
    }
  },
  
  // Eliminar un proyecto
  async deleteProject(id: string): Promise<boolean> {
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', id);
      
    if (error) {
      console.error(`Error al eliminar el proyecto ${id}:`, error);
      return false;
    }
    
    return true;
  },

  // Gestión de hitos del proyecto
  async createMilestone(milestone: Omit<ProjectMilestone, 'id' | 'created_at' | 'updated_at'>): Promise<ProjectMilestone | null> {
    try {
      const { data, error } = await supabase
        .from('project_milestones')
        .insert(milestone)
        .select()
        .single();

      if (error) {
        console.error('Error al crear hito:', error);
        throw error;
      }

      return data as ProjectMilestone;
    } catch (error) {
      console.error('Error en createMilestone:', error);
      throw error;
    }
  },

  async getMilestonesByProject(projectId: string): Promise<ProjectMilestone[]> {
    const { data, error } = await supabase
      .from('project_milestones')
      .select('*')
      .eq('project_id', projectId)
      .order('due_date', { ascending: true });

    if (error) {
      console.error(`Error al obtener hitos del proyecto ${projectId}:`, error);
      return [];
    }

    return data as ProjectMilestone[];
  },

  async createStakeholder(stakeholder: Omit<ProjectStakeholder, 'id' | 'created_at' | 'updated_at'>): Promise<ProjectStakeholder | null> {
    try {
      const { data, error } = await supabase
        .from('project_stakeholders')
        .insert(stakeholder)
        .select()
        .single();

      if (error) {
        console.error('Error al crear stakeholder:', error);
        throw error;
      }

      return data as ProjectStakeholder;
    } catch (error) {
      console.error('Error en createStakeholder:', error);
      throw error;
    }
  },

  async getStakeholdersByProject(projectId: string): Promise<ProjectStakeholder[]> {
    const { data, error } = await supabase
      .from('project_stakeholders')
      .select('*')
      .eq('project_id', projectId);

    if (error) {
      console.error(`Error al obtener stakeholders del proyecto ${projectId}:`, error);
      return [];
    }

    return data as ProjectStakeholder[];
  }
};
